import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ReferenceInput,
  AutocompleteInput,
  FormDataConsumer,
  withDataProvider,
  REDUX_FORM_NAME,
  GET_ONE,
  GET_LIST,
} from 'react-admin';
import { change as changeFieldValue } from 'redux-form';
import {
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Typography,
  Divider,
} from '@material-ui/core';
import LaunchIcon from '@material-ui/icons/Launch';
import VideoLibraryIcon from '@material-ui/icons/VideoLibrary';

const formatOptionText = (choice) => {
  if (!choice) {
    return '';
  }

  const title = choice.originalName || 'Untitled video';
  const muxId = choice.muxPlaybackId ? ` • ${choice.muxPlaybackId}` : '';

  return `${title}${muxId}`;
};

const filterToQuery = (searchText) => ({
  search: searchText || undefined,
});

const alwaysRenderSuggestions = (inputValue) => !!inputValue && inputValue.trim().length > 0;

const VideoPreview = ({ video }) => {
  if (!video) {
    return null;
  }

  return (
    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center' }}>
      <div>
        <div style={{ fontWeight: 600 }}>{video.originalName || 'Selected video'}</div>
        {video.muxPlaybackId ? (
          <div style={{ fontSize: 12 }}>Mux ID: {video.muxPlaybackId}</div>
        ) : null}
      </div>
      {video.muxPlaybackId ? (
        <IconButton
          href={`https://stream.mux.com/${video.muxPlaybackId}/low.mp4`}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          title="Open stream in new tab"
        >
          <LaunchIcon fontSize="small" />
        </IconButton>
      ) : null}
    </div>
  );
};

const PAGE_SIZE = 10;

const VideoPickerDialog = ({ open, onClose, onSelect, dataProvider }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');

  const totalPages = useMemo(() => (total ? Math.ceil(total / PAGE_SIZE) : 1), [total]);

  const fetchVideos = useCallback(async () => {
    if (!open) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await dataProvider(GET_LIST, 'file/video', {
        pagination: { page, perPage: PAGE_SIZE },
        sort: { field: 'createdAt', order: 'DESC' },
        filter: query ? { search: query } : {},
      });
      setVideos(response?.data || []);
      setTotal(response?.total || 0);
    } catch (err) {
      setError('Unable to load videos. Please try again.');
      setVideos([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [dataProvider, open, page, query]);

  useEffect(() => {
    if (!open) {
      return;
    }
    fetchVideos();
  }, [open, fetchVideos]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setQuery(searchInput.trim());
  };

  const handleSelectVideo = (video) => {
    onSelect(video);
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  const handleExited = () => {
    setVideos([]);
    setSearchInput('');
    setQuery('');
    setPage(1);
    setError('');
  };

  return (
    <Dialog open={open} onClose={handleClose} onExited={handleExited} maxWidth="sm" fullWidth>
      <DialogTitle>Select a Video from Media</DialogTitle>
      <DialogContent>
        <form
          onSubmit={handleSearchSubmit}
          style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}
        >
          <TextField
            fullWidth
            label="Search by name or Mux ID"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            variant="outlined"
            size="small"
          />
          <Button type="submit" color="primary" variant="contained">
            Search
          </Button>
        </form>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
            <CircularProgress size={24} />
          </div>
        ) : (
          <>
            {error ? (
              <Typography color="error" style={{ marginBottom: 12 }}>
                {error}
              </Typography>
            ) : null}
            {videos.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No videos found. Try a different search or upload via Media → Videos.
              </Typography>
            ) : (
              <List dense>
                {videos.map((video) => (
                  <Fragment key={video.id}>
                    <ListItem
                      button
                      alignItems="flex-start"
                      onClick={() => handleSelectVideo(video)}
                    >
                      <ListItemText
                        primary={video.originalName || video.fileName || `Video #${video.id}`}
                        secondary={[
                          video.muxPlaybackId ? `Mux ID: ${video.muxPlaybackId}` : null,
                          video.duration ? `Duration: ${video.duration}s` : null,
                        ]
                          .filter(Boolean)
                          .join(' • ')}
                      />
                      <ListItemSecondaryAction>
                        {video.muxPlaybackId ? (
                          <IconButton
                            href={`https://stream.mux.com/${video.muxPlaybackId}/low.mp4`}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            title="Open stream in new tab"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <LaunchIcon fontSize="small" />
                          </IconButton>
                        ) : null}
                        <Button
                          color="primary"
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleSelectVideo(video);
                          }}
                        >
                          Select
                        </Button>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider component="li" />
                  </Fragment>
                ))}
              </List>
            )}
            {total > PAGE_SIZE ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 12,
                }}
              >
                <Button disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  Previous
                </Button>
                <Typography variant="body2">
                  Page {page} / {totalPages}
                </Typography>
                <Button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => (p < totalPages ? p + 1 : p))}
                >
                  Next
                </Button>
              </div>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const VideoPreviewFromValues = ({ showPreview, dataProvider }) => (
  <FormDataConsumer>
    {({ formData }) => (
      <VideoPreviewContent
        showPreview={showPreview}
        dataProvider={dataProvider}
        selectedVideo={formData?.video}
        selectedVideoId={formData?.videoId}
      />
    )}
  </FormDataConsumer>
);

const VideoPreviewContent = ({ showPreview, selectedVideo, selectedVideoId, dataProvider }) => {
  const [previewVideo, setPreviewVideo] = useState(selectedVideo || null);
  const lastLoadedId = useRef(selectedVideo?.id || null);

  useEffect(() => {
    let isMounted = true;
    const cleanup = () => {
      isMounted = false;
    };

    if (!showPreview) {
      return cleanup;
    }

    if (selectedVideo && selectedVideo.id !== lastLoadedId.current) {
      setPreviewVideo(selectedVideo);
      lastLoadedId.current = selectedVideo.id;
      return cleanup;
    }

    if (!selectedVideoId) {
      if (previewVideo) {
        setPreviewVideo(null);
      }
      lastLoadedId.current = null;
      return cleanup;
    }

    if (lastLoadedId.current === selectedVideoId) {
      return cleanup;
    }

    lastLoadedId.current = selectedVideoId;
    dataProvider(GET_ONE, 'file/video', { id: selectedVideoId })
      .then(({ data }) => {
        if (isMounted) {
          setPreviewVideo(data);
        }
      })
      .catch(() => {
        if (isMounted) {
          setPreviewVideo(null);
        }
      });

    return cleanup;
  }, [showPreview, selectedVideo, selectedVideoId, dataProvider, previewVideo]);

  if (!showPreview || !previewVideo) {
    return null;
  }

  return <VideoPreview video={previewVideo} />;
};

const DispatchBridge = ({ onDispatch }) => (
  <FormDataConsumer>
    {({ dispatch }) => {
      if (dispatch) {
        onDispatch(dispatch);
      }
      return null;
    }}
  </FormDataConsumer>
);

const AchievementVideoInput = ({ helperText, showPreview = true, dataProvider }) => {
  const [isPickerOpen, setPickerOpen] = useState(false);
  const formDispatchRef = useRef(null);

  const handleDispatchUpdate = useCallback((dispatch) => {
    if (formDispatchRef.current !== dispatch) {
      formDispatchRef.current = dispatch;
    }
  }, []);

  const handleSelectFromDialog = useCallback((video) => {
    if (formDispatchRef.current) {
      formDispatchRef.current(changeFieldValue(REDUX_FORM_NAME, 'videoId', video?.id || null));
    }
  }, []);

  return (
    <>
      <ReferenceInput
        source="videoId"
        reference="file/video"
        allowEmpty
        perPage={25}
        label="Associated Video"
      >
        <AutocompleteInput
          optionText={formatOptionText}
          optionValue="id"
          helperText={helperText}
          allowEmpty
          resettable
          filterToQuery={filterToQuery}
          shouldRenderSuggestions={alwaysRenderSuggestions}
          options={{ fullWidth: true, placeholder: 'Search videos...' }}
        />
      </ReferenceInput>
      <Button
        type="button"
        variant="outlined"
        size="small"
        startIcon={<VideoLibraryIcon />}
        onClick={() => setPickerOpen(true)}
        style={{ marginTop: 8 }}
      >
        Browse media library
      </Button>
      <DispatchBridge onDispatch={handleDispatchUpdate} />
      <VideoPreviewFromValues showPreview={showPreview} dataProvider={dataProvider} />
      <VideoPickerDialog
        open={isPickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handleSelectFromDialog}
        dataProvider={dataProvider}
      />
    </>
  );
};

export default withDataProvider(AchievementVideoInput);
